import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import { DENIAL_REASON_DISPLAY_NAMES, Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from './MetricDetailLayout';
import { buildBreakdownRows, readReferralRate } from './rollupHelpers';
import { RateMetricTiles } from './statTiles';
import useSlideoutSharedData, { useSlideoutDimensionCounts } from './useSlideoutSharedData';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

const ROW_HEIGHT = 32;

export default function DenialRateDetailHeader({ loading, rollups }: Props) {
  const { topDenyingReceivingGroup, topDenyingPlan, topByLoading, partnerLabel } = useSlideoutSharedData();
  const { rate, count, total } = readReferralRate(rollups, Metrics.DENIAL_RATE);

  const {
    names: reasonNames,
    counts: reasonCounts,
    loading: reasonsLoading,
  } = useSlideoutDimensionCounts(Dimensions.DENIAL_REASON, 'referral-slideout-denial-reasons');

  const reasons = useMemo(
    () =>
      buildBreakdownRows(reasonNames, reasonCounts, {
        displayNameMap: DENIAL_REASON_DISPLAY_NAMES,
        sortDesc: true,
      }),
    [reasonNames, reasonCounts]
  );

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const maxCount = reasons.reduce((m, r) => Math.max(m, r.count), 0);

    return {
      chart: {
        type: 'bar',
        style: { fontFamily: 'Lato' },
        height: reasons.length * ROW_HEIGHT,
        spacingTop: 0,
        spacingBottom: 0,
        marginRight: 24,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: { enabled: false },
      xAxis: {
        categories: reasons.map((r) => r.name),
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '12px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineWidth: 0,
        lineWidth: 0,
        labels: { enabled: false },
        tickLength: 0,
        max: maxCount || undefined,
        opposite: true,
      },
      tooltip: { backgroundColor: colors.chartTooltipBackground, style: { color: colors.white }, padding: 12 },
      plotOptions: {
        series: { grouping: false, animation: false, groupPadding: 0, pointPadding: 0 },
        bar: {
          pointWidth: 10,
          borderRadius: { radius: 3, scope: 'point', where: 'all' },
          dataLabels: { enabled: false },
        },
      },
      series: [
        {
          type: 'bar',
          name: 'Track',
          data: reasons.map(() => maxCount),
          color: colors.black5,
          enableMouseTracking: false,
          showInLegend: false,
        },
        {
          type: 'bar',
          name: 'Denials',
          data: reasons.map((r) => ({
            y: r.count,
            color: colors.accentRed,
            opacity: maxCount > 0 ? r.count / maxCount : 1,
          })),
        },
      ],
    };
  }, [reasons]);

  return (
    <MetricDetailLayout
      tiles={
        <RateMetricTiles
          rateLabel='Avg. Denial Rate'
          rateTone='red'
          rate={rate}
          count={count}
          total={total}
          ratePhrase='denial rate'
          loading={loading}
          topByLoading={topByLoading}
          topReceivingLabel={`Top Denying ${partnerLabel}`}
          topReceiving={count > 0 ? topDenyingReceivingGroup : null}
          topPlanLabel='Top Denying Plan'
          topPlan={count > 0 ? topDenyingPlan : null}
        />
      }
      chartTitle='Denial Reasons'
      chartLoading={reasonsLoading}
      isEmpty={reasons.length === 0}
      emptyMessage='No denial reasons recorded for this period.'>
      <ChartRow>
        <ChartFill>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </ChartFill>
        <NumberColumn $rows={reasons.length} $rowHeight={ROW_HEIGHT}>
          {reasons.map((r, i) => (
            <NumberCell key={i}>{r.count}</NumberCell>
          ))}
        </NumberColumn>
      </ChartRow>
    </MetricDetailLayout>
  );
}

const ChartRow = styled.div`
  display: flex;
  align-items: stretch;
`;

const ChartFill = styled.div`
  flex: 1;
  min-width: 0;
`;

const NumberColumn = styled.div<{ $rows: number; $rowHeight: number }>`
  display: grid;
  grid-template-rows: ${({ $rows, $rowHeight }) => `repeat(${$rows}, ${$rowHeight}px)`};
  width: 22px;
`;

const NumberCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.black75};
`;
